import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Button, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Grid,
    Alert,
    CircularProgress,
    Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import BASE_URL from '../config';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useOrg } from '../contexts/OrgContext';
import { toast } from 'react-toastify';

const GeoJSONUploadModern = () => {
    const { isOrgAdmin, isSuperAdmin } = usePermissions();
    const { getToken } = useAuth();
    const { selectedOrgId } = useOrg();

    const [sites, setSites] = useState([]);
    const [subSites, setSubSites] = useState([]);
    const [siteId, setSiteId] = useState('');
    const [subSiteId, setSubSiteId] = useState('');
    
    const [file, setFile] = useState(null);
    const [featureCount, setFeatureCount] = useState(0);
    const [propertiesKeys, setPropertiesKeys] = useState([]);
    
    const [mapping, setMapping] = useState({
        health: { ndvi: '', health: '' },
        height: { height_m: '' },
        species: { species: '' },
        waterStress: { water_stress: '' }
    });

    const [loading, setLoading] = useState(false);
    const [successRunId, setSuccessRunId] = useState(null);
    const [skipStats, setSkipStats] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchSites = async () => {
            if (!selectedOrgId) return;
            try {
                const token = await getToken();
                const res = await axios.get(`${BASE_URL}/api/sites`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { organisation_id: selectedOrgId }
                });
                
                // Ensure we get the array
                const sitesData = res.data || [];
                const parsedSites = Array.isArray(sitesData) ? sitesData : (sitesData.sites || []);
                // Filter to only show eco-sites (asset_type_id == 1) if the data is available
                const ecoSites = parsedSites.filter(s => s.asset_type_id === undefined || s.asset_type_id === 1);
                setSites(ecoSites);
            } catch (err) {
                console.error("Failed to fetch sites", err);
                toast.error("Failed to fetch sites.");
            }
        };
        fetchSites();
    }, [selectedOrgId, getToken]);

    useEffect(() => {
        const fetchSubSites = async () => {
            if (!siteId) {
                setSubSites([]);
                return;
            }
            try {
                const token = await getToken();
                // Correct sub-sites API URL
                const res = await axios.get(`${BASE_URL}/api/sites/${siteId}/sub-sites`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSubSites(res.data || []);
            } catch (err) {
                console.error("Failed to fetch sub-sites", err);
                // Don't toast error here, it might just mean no sub-sites exist for this site
                setSubSites([]);
            }
        };
        fetchSubSites();
    }, [siteId, getToken]);

    if (!isOrgAdmin() && !isSuperAdmin) {
        return (
            <Box p={3}>
                <Alert severity="error">You do not have permission to access this page. Only Admins can upload tree assets.</Alert>
            </Box>
        );
    }

    if (isSuperAdmin && !selectedOrgId) {
        return (
            <Box p={3}>
                <Alert severity="warning">Please select an organization from the sidebar to continue.</Alert>
            </Box>
        );
    }

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const geojson = JSON.parse(event.target.result);
                    if (geojson.type === "FeatureCollection" && Array.isArray(geojson.features)) {
                        setFeatureCount(geojson.features.length);
                        
                        // Scan ALL features for unique property keys
                        const keys = new Set();
                        for (const feature of geojson.features) {
                            if (feature.properties) {
                                Object.keys(feature.properties).forEach(k => keys.add(k));
                            }
                        }
                        setPropertiesKeys(Array.from(keys));
                    } else {
                        toast.error("Invalid GeoJSON format. Expected FeatureCollection.");
                        resetFile();
                    }
                } catch (err) {
                    toast.error("Failed to parse JSON file. Check if the file is valid JSON.");
                    resetFile();
                }
            };
            reader.readAsText(selectedFile);
        }
    };

    const resetFile = () => {
        setFile(null);
        setFeatureCount(0);
        setPropertiesKeys([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleMappingChange = (category, field, value) => {
        setMapping(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [field]: value
            }
        }));
    };

    const handleSubmit = async () => {
        if (!siteId) {
            toast.error("Site ID is required.");
            return;
        }
        if (!file) {
            toast.error("Please select a valid GeoJSON file.");
            return;
        }

        setLoading(true);
        setSuccessRunId(null);
        setSkipStats(null);
        
        try {
            const token = await getToken();
            const formData = new FormData();
            formData.append('site_id', siteId);
            // Include organisation_id in form data just in case, but rely on query param for middleware
            formData.append('organisation_id', selectedOrgId); 
            if (subSiteId) {
                formData.append('sub_site_id', subSiteId);
            }
            formData.append('mappingConfig', JSON.stringify(mapping));
            formData.append('file', file);

            // Append organisation_id to URL so verifyFirebaseToken can read it before multer parses the body
            const res = await axios.post(`${BASE_URL}/api/upload-trees?organisation_id=${selectedOrgId}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(res.data.message || "Tree assets uploaded successfully!");
            setSuccessRunId(res.data.run_id);
            setSkipStats(res.data.stats || null);

            // Reset Form
            resetFile();
            setMapping({
                health: { ndvi: '', health: '' },
                height: { height_m: '' },
                species: { species: '' },
                waterStress: { water_stress: '' }
            });
            setSiteId('');
            setSubSiteId('');

        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || "Failed to upload trees.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={4} maxWidth="1200px" mx="auto">
            <Typography variant="h4" mb={3} fontWeight="bold" color="primary">
                Upload Tree Assets (GeoJSON)
            </Typography>

            {successRunId && (
                <Alert 
                    severity="success" 
                    sx={{ mb: 3 }}
                    action={
                        <Button color="inherit" size="small" component={Link} to={`/ecological?run_id=${successRunId}`}>
                            View on Map
                        </Button>
                    }
                >
                    <Typography variant="body1">
                        Upload successful! The run ID for this batch is: <strong>{successRunId}</strong>
                    </Typography>
                    {skipStats && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Inserted: {skipStats.inserted} features. 
                            Skipped: {skipStats.skipped?.missingGeometry || 0} missing geometry, {skipStats.skipped?.invalidGeometryType || 0} points/lines.
                        </Typography>
                    )}
                </Alert>
            )}

            <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                            <InputLabel>Site</InputLabel>
                            <Select
                                value={siteId}
                                label="Site"
                                onChange={(e) => {
                                    setSiteId(e.target.value);
                                    setSubSiteId('');
                                }}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {sites.map(s => (
                                    <MenuItem key={s.site_id} value={s.site_id}>
                                        {s.name} ({s.site_id})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth disabled={!siteId || subSites.length === 0}>
                            <InputLabel>Sub Site (Optional)</InputLabel>
                            <Select
                                value={subSiteId}
                                label="Sub Site (Optional)"
                                onChange={(e) => setSubSiteId(e.target.value)}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {subSites.map(ss => (
                                    <MenuItem key={ss.sub_site_id} value={ss.sub_site_id}>
                                        {ss.name} ({ss.sub_site_id})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box border="2px dashed #ccc" borderRadius={2} p={4} textAlign="center">
                            <input
                                accept=".geojson,.json"
                                style={{ display: 'none' }}
                                id="geojson-upload"
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                            <label htmlFor="geojson-upload">
                                <Button variant="contained" component="span" color="secondary">
                                    Select GeoJSON File
                                </Button>
                            </label>
                            {file && (
                                <Box mt={2}>
                                    <Typography color="textSecondary">{file.name} ({featureCount} features)</Typography>
                                    <Button size="small" color="error" onClick={resetFile} sx={{ mt: 1 }}>Clear File</Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {propertiesKeys.length > 0 && (
                <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                    <Typography variant="h5" mb={3} fontWeight="bold">
                        Map Attributes
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={4}>
                        {/* Tree Health */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom color="textSecondary">Tree Health</Typography>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>NDVI Value</InputLabel>
                                <Select
                                    value={mapping.health.ndvi}
                                    label="NDVI Value"
                                    onChange={(e) => handleMappingChange('health', 'ndvi', e.target.value)}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {propertiesKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Health Status Enum (low, medium, high, critical)</InputLabel>
                                <Select
                                    value={mapping.health.health}
                                    label="Health Status Enum (low, medium, high, critical)"
                                    onChange={(e) => handleMappingChange('health', 'health', e.target.value)}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {propertiesKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Tree Height & Species */}
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom color="textSecondary">Other Metrics</Typography>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Height (m)</InputLabel>
                                <Select
                                    value={mapping.height.height_m}
                                    label="Height (m)"
                                    onChange={(e) => handleMappingChange('height', 'height_m', e.target.value)}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {propertiesKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                            </FormControl>
                            
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Species Name</InputLabel>
                                <Select
                                    value={mapping.species.species}
                                    label="Species Name"
                                    onChange={(e) => handleMappingChange('species', 'species', e.target.value)}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {propertiesKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                            </FormControl>
                            
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Water Stress Enum (low, medium, high, critical)</InputLabel>
                                <Select
                                    value={mapping.waterStress.water_stress}
                                    label="Water Stress Enum (low, medium, high, critical)"
                                    onChange={(e) => handleMappingChange('waterStress', 'water_stress', e.target.value)}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {propertiesKeys.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Box mt={4} textAlign="right">
                        <Button 
                            variant="contained" 
                            color="primary" 
                            size="large"
                            onClick={handleSubmit}
                            disabled={loading || !siteId}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Upload & Process'}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default GeoJSONUploadModern;
